<script setup lang="ts">
import type { Component } from "vue";
import Button from "~/components/inputs/Button.vue";

const props = defineProps({
  show: { type: Boolean, default: false },
  icon: { type: String, required: false },
  iconClass: { type: String, default: "" },
  cta: { type: String, required: false },
  customClass: { type: String, default: "" },
  mainClass: { type: String, default: "" },
});

const emit = defineEmits(["clickOutside", "click", "closed"]);

const modalElement: Component = useTemplateRef('modal-element')

const Icon = computed(() =>
  defineAsyncComponent(() => import(`~/components/icons/${props.icon}.vue`)),
);

const loopFocus = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    event.preventDefault()
    modalElement.value.focus()
  }
}

watch(() => props.show, (isVisible) => {
  if (isVisible) {
    nextTick(() => {
      modalElement.value.focus()
    })
  }
})

const handlePointerDown = () => {
  emit("clickOutside")
};
</script>

<template>
  <transition name="modal" @after-leave="$emit('closed')">
    <div
      v-if="show"
      :class="[
        mainClass,
        'fixed bg-black bg-opacity-25 inset-0 flex items-center justify-center z-40',
      ]"
      @click="handlePointerDown"
      @touchstart="handlePointerDown"
    >
      <div
        @click.stop
        @touchstart.stop
        role="alert"
        tabindex="0"
        :class="[customClass, 'modal-wrapper bg-white p-4 mx-4 text-center rounded-3xl mb-64 max-w-xl text-gray-700 shadow-xl']"
      >
        <div ref="modal-element" tabindex="0"></div>
        <div
          v-if="icon"
          :class="[iconClass, 'mx-auto inline-block align-middle my-3']"
        >
          <component :is="Icon" v-bind="{ width: 66, height: 66 }" />
        </div>
        <div>
          <slot name="title"></slot>
        </div>
        <div v-if="$slots.text" class="my-6">
          <slot name="text"></slot>
        </div>

        <div v-if="$slots.textV2">
          <slot name="textV2"></slot>
        </div>
        <div v-if="cta" :class="[$slots.button ? '' : 'mb-4']">
          <div @click="$emit('click')" v-if="$slots.button" @keydown="loopFocus" >
            <slot name="button" />
          </div>

          <Button
            v-else
            type="button"
            @click="emit('click')"
            @keydown="loopFocus"
          >
            {{ cta }}
          </Button>
        </div>
        <div>
          <slot name="bottom" />
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped></style>
